// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import { ActionType, IAction, raiseAction, IActionSortTable } from "actions";
import { AlarmState, IAlarmEntry } from "shared/types/globtypes";
import { IState } from "states/states";
import { ITableSort } from "states/guistates";
import { isSet } from "shared/utils/utils";
import { makeSortHeader } from "guiutils";

import AlarmEntry from "view/comp/AlarmEntry";
import SiteBar, { ISiteBarSearchFieldProps } from "view/comp/SiteBar";

// uses webpack definitions
declare const IS_CLOUD: boolean;

// sort key of table
const TABLE_NAME = "t_alarm";

interface IAlarmExtProps {
  isDashboard?: boolean;
}

interface IAlarmProps {
  isEditable: boolean;
  alarms: IAlarmEntry[];
  tableSort: ITableSort | undefined;
  filterText: string;
}

const mapStateToProps = (state: IState) => {
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IAlarmProps = {
    isEditable:
      (!IS_CLOUD && state.site.user?.secId === "admin") ||
      (IS_CLOUD && (curUser?.accessRights.ackAlarms ?? false)),
    alarms: state.data.alarms,
    tableSort: state.gui.tableSort[TABLE_NAME] ?? {
      column: "alarmTimestamp",
      isSortAscending: false,
    },
    filterText: state.gui.searchFilterText ?? "",
  };
  return props;
};

interface IAlarmDispatch {
  onSort: (sort: IActionSortTable) => void;
  onAckAll: () => void;
}

const messages = defineMessages({
  placeholderSearchField: {
    id: "Search for alarms...",
  },
  ackAllTooltip: {
    id: "acknowledge all alarms",
  },
});

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IAlarmDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onAckAll: () => {
      dispatch(raiseAction(ActionType.ACT_ACK_ALL_ALARMS_REQ));
    },
  };
  return dispatchProps;
};

class AlarmComponent extends React.Component<IAlarmProps & IAlarmExtProps & IAlarmDispatch> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const sortCol = this.props.tableSort?.column as keyof IAlarmEntry | undefined;
    // data state is immutable so we need to copy it before sorting
    const alarmIds = Array.from(Array(this.props.alarms.length).keys()).sort((a, b) => {
      const keyA = this.props.alarms[a][sortCol!];
      const keyB = this.props.alarms[b][sortCol!];
      let sort =
        isSet(keyA) && isSet(keyB) ?
          keyA > keyB ? 1
          : keyA < keyB ? -1
          : 0
        : isSet(keyB) ? 1
        : -1;

      // secondary sort by timestamp if primary sort is equal
      if (sort === 0) {
        const timeA = this.props.alarms[a].alarmTimestamp;
        const timeB = this.props.alarms[b].alarmTimestamp;
        sort =
          timeA > timeB ? 1
          : timeA < timeB ? -1
          : 0;
      }

      return (this.props.tableSort?.isSortAscending !== false ? sort : -sort) as number;
    });
    const alarmRows = alarmIds.map((curItem) => {
      return (
        <AlarmEntry
          key={curItem}
          index={curItem}
          isEditable={this.props.isEditable}
          filterText={this.props.filterText}
        />
      );
    });
    const searchFieldProps: ISiteBarSearchFieldProps = {
      placeholder: messages.placeholderSearchField,
    };

    return (
      <>
        {!this.props.isDashboard && (
          <SiteBar showPeriodSelect={false} searchFieldProps={searchFieldProps} />
        )}
        {this.props.isDashboard !== true && (
          <div className="tableSearchHeader">
            <h1>
              <FormattedMessage id="Alarms" />
            </h1>
          </div>
        )}
        <div id="alarm">
          {alarmIds.length ?
            <table className="datatable fullwidthwhite">
              <thead>
                <tr>
                  {makeSortHeader(
                    TABLE_NAME,
                    "alarmTimestamp",
                    <FormattedMessage id="Timestamp" />,
                    this.props.tableSort,
                    this.props.onSort,
                    ["center"],
                  )}
                  {makeSortHeader(
                    TABLE_NAME,
                    "source",
                    <FormattedMessage id="Alarm" />,
                    this.props.tableSort,
                    this.props.onSort,
                  )}
                  {makeSortHeader(
                    TABLE_NAME,
                    "state",
                    <FormattedMessage id="State" />,
                    this.props.tableSort,
                    this.props.onSort,
                  )}
                  <th className="optional-sm">
                    <FormattedMessage id="Further Information" />
                  </th>
                  {this.props.isEditable && (
                    <th className="alarmAck">
                      {this.props.alarms.some(
                        (curAlarm) =>
                          ![AlarmState.Ackd, AlarmState.AckdActive].includes(curAlarm.state),
                      ) && (
                        <Button
                          variant="secondary"
                          className="me-1"
                          title={this.context.formatMessage(messages.ackAllTooltip)}
                          onClick={this.props.onAckAll}
                        >
                          <FormattedMessage id="Acknowledge all" />
                        </Button>
                      )}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>{alarmRows}</tbody>
            </table>
          : <p className="alert alert-success">
              <FormattedMessage id="No alarms pending." />
            </p>
          }
        </div>
      </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(AlarmComponent);
